// src/services/adminService.ts
import api from './api';

// ===================================
// INTERFACES & TYPES
// ===================================

export interface UserWithAdminInfo {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  merchant_phone: string | null;
  is_farmer: boolean;
  is_admin: boolean;
  is_super_admin: boolean;
  is_active: boolean;
  created_at: string;
  admin_level: 'user' | 'admin' | 'super_admin';
  total_actions: number;
  last_login: string | null;
}

export interface AdminStats {
  total_users: number;
  total_farmers: number;
  total_customers: number;
  total_admins: number;
  total_super_admins: number;
  active_users: number;
  inactive_users: number;
  recent_registrations: number;
  total_orders: number;
  total_products: number;
  recent_activities: number;
}

export interface AdminActivityLog {
  id: number;
  admin_id: number;
  action_type: string;
  target_user_id: number | null;
  target_resource_type: string | null;
  target_resource_id: number | null;
  description: string;
  log_metadata: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  admin_name: string;
  target_user_name: string | null;
}

export interface AdminRoleUpdate {
  user_id: number;
  action: 'promote_to_admin' | 'demote_from_admin' | 'promote_to_super_admin' | 'demote_from_super_admin';
  reason?: string;
}

export interface BulkUserAction {
  user_ids: number[];
  action: 'activate' | 'deactivate' | 'delete';
  reason?: string;
}

export interface AdminHierarchy {
  super_admins: Array<{
    id: number;
    name: string;
    email: string;
    phone: string;
    is_super_admin: boolean;
    created_at: string;
    recent_actions: number;
    total_actions: number;
  }>;
  regular_admins: Array<{
    id: number;
    name: string;
    email: string;
    phone: string;
    is_super_admin: boolean;
    created_at: string;
    recent_actions: number;
    total_actions: number;
  }>;
  total_super_admins: number;
  total_regular_admins: number;
}

export interface SystemSecurity {
  security_status: 'healthy' | 'at_risk';
  super_admin_exists: boolean;
  total_super_admins: number;
  recent_critical_actions: number;
  inactive_admin_accounts: number;
  recommendations: string[];
}

export interface AdminCreate {
  name: string;
  email: string;
  password: string;
  phone?: string;
  merchant_phone?: string;
  is_farmer: boolean;
  is_customer: boolean;
  make_super_admin: boolean;
}

// ===================================
// SERVICE FUNCTIONS
// ===================================

class AdminService {
  
  // ===================================
  // USERS MANAGEMENT (Enhanced)
  // ===================================
  
  async getUsers(params?: {
    skip?: number;
    limit?: number;
    search?: string;
    role_filter?: 'admin' | 'farmer' | 'customer' | 'super_admin';
    status_filter?: 'active' | 'inactive';
  }): Promise<UserWithAdminInfo[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.skip) queryParams.append('skip', params.skip.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role_filter) queryParams.append('role_filter', params.role_filter);
    if (params?.status_filter) queryParams.append('status_filter', params.status_filter);
    
    const response = await api.get(`/admin/users?${queryParams.toString()}`);
    return response.data;
  }

  async getUser(userId: number): Promise<UserWithAdminInfo> {
    const response = await api.get(`/admin/users/${userId}`);
    return response.data;
  }

  async deleteUser(userId: number, reason?: string): Promise<void> {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    await api.delete(`/admin/users/${userId}${params}`);
  }

  async toggleUserActive(userId: number, reason?: string): Promise<void> {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    await api.patch(`/admin/users/${userId}/toggle-active${params}`);
  }

  async toggleUserAdmin(userId: number, reason?: string): Promise<void> {
    const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    await api.patch(`/admin/users/${userId}/toggle-admin${params}`);
  }

  // ===================================
  // ADMIN HIERARCHY
  // ===================================

  async createAdmin(adminData: AdminCreate): Promise<UserWithAdminInfo> {
    const response = await api.post('/admin/admins', adminData);
    return response.data;
  }

  async updateAdminRole(roleUpdate: AdminRoleUpdate): Promise<void> {
    await api.patch(`/admin/users/${roleUpdate.user_id}/admin-role`, roleUpdate);
  }

  async getAdminHierarchy(): Promise<AdminHierarchy> {
    const response = await api.get('/admin/admin-hierarchy');
    return response.data;
  }

  // ===================================
  // BULK ACTIONS
  // ===================================

  async bulkUserActions(bulkAction: BulkUserAction): Promise<{
    results: Array<{
      user_id: number;
      success: boolean;
      message: string;
    }>;
    total_processed: number;
    successful: number;
    failed: number;
  }> {
    const response = await api.post('/admin/users/bulk-actions', bulkAction);
    return response.data;
  }

  // ===================================
  // ACTIVITY LOGS
  // ===================================

  async getActivityLogs(params?: {
    admin_id?: number;
    action_type?: string;
    target_user_id?: number;
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    logs: AdminActivityLog[];
    total: number;
    has_more: boolean;
  }> {
    const queryParams = new URLSearchParams();
    
    if (params?.admin_id) queryParams.append('admin_id', params.admin_id.toString());
    if (params?.action_type) queryParams.append('action_type', params.action_type);
    if (params?.target_user_id) queryParams.append('target_user_id', params.target_user_id.toString());
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());
    
    const response = await api.get(`/admin/activity-logs?${queryParams.toString()}`);
    return response.data;
  }

  // ===================================
  // STATISTICS
  // ===================================

  async getAdminStats(): Promise<AdminStats> {
    const response = await api.get('/admin/stats');
    return response.data;
  }

  // ===================================
  // SECURITY
  // ===================================

  async getSystemSecurity(): Promise<SystemSecurity> {
    const response = await api.get('/admin/system-security');
    return response.data;
  }

  // ===================================
  // UTILITY METHODS
  // ===================================

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getAdminLevelLabel(level: string): string {
    switch (level) {
      case 'super_admin': return 'Super Admin';
      case 'admin': return 'Admin';
      default: return 'Utilisateur';
    }
  }

  getAdminLevelColor(level: string): string {
    switch (level) {
      case 'super_admin': return 'bg-red-100 text-red-800';
      case 'admin': return 'bg-purple-100 text-purple-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  }

  getActionTypeLabel(actionType: string): string {
    const labels: Record<string, string> = {
      'create_user': 'Créer utilisateur',
      'update_user': 'Modifier utilisateur',
      'delete_user': 'Supprimer utilisateur',
      'promote_to_admin': 'Promouvoir admin',
      'demote_from_admin': 'Rétrograder admin',
      'promote_to_super_admin': 'Promouvoir super admin',
      'demote_from_super_admin': 'Rétrograder super admin',
      'bulk_activate': 'Activation en masse',
      'bulk_deactivate': 'Désactivation en masse',
      'view_users_list': 'Consulter utilisateurs',
      'view_admin_stats': 'Consulter statistiques',
      'view_activity_logs': 'Consulter logs',
      'delete_product': 'Supprimer produit',
      'migration_promote_super_admin': 'Migration: Promotion super admin',
      'migration_create_super_admin': 'Migration: Création super admin'
    };
    return labels[actionType] || actionType;
  }

  getSecurityStatusColor(status: string): string {
    switch (status) {
      case 'healthy': return 'text-green-600';
      case 'at_risk': return 'text-red-600';
      default: return 'text-yellow-600';
    }
  }

  getSecurityStatusLabel(status: string): string {
    switch (status) {
      case 'healthy': return 'Sécurisé';
      case 'at_risk': return 'À risque';
      default: return 'Inconnu';
    }
  }

  // Validation des permissions côté client
  canPromoteToSuperAdmin(currentUserLevel: string): boolean {
    return currentUserLevel === 'super_admin';
  }

  canDemoteAdmin(currentUserLevel: string, targetLevel: string): boolean {
    return currentUserLevel === 'super_admin' && targetLevel !== 'super_admin';
  }

  canDeleteUser(currentUserLevel: string): boolean {
    return currentUserLevel === 'super_admin';
  }

  canViewLogs(currentUserLevel: string): boolean {
    return ['admin', 'super_admin'].includes(currentUserLevel);
  }

  canViewSecurity(currentUserLevel: string): boolean {
    return currentUserLevel === 'super_admin';
  }
}

export default new AdminService();